import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import {
  debounce,
  platformTransitionStyle,
  triggerSentryError,
  ComcastErrorType,
} from '../../../../shared/helpers.js'
import {
  getAvailableSeasons,
  getExtrasInSeasons,
  getMoreExtras,
  getPaginatedEpisodes,
  getSeasonDetails,
} from '../../actions/contentActions'
import focusable from '../../components/Focusable/Focusable'
import LogoHeader from '../../components/LogoHeader/LogoHeader'
import StdButton from '../../components/StdButton/StdButton.js'
import VideoList from '../../components/VideoList/VideoList'
import tts from '../../lib/TTS'
import Spinner from '../../components/Spinner/Spinner.js'
import { store } from '../../store'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import { withRouter } from '../../../../shared/components/WithRouter/WithRouter'

const OFFSET = 100
const defaultState = {
  videoSections: [],
  seasonDescription: '',
  seasonDescriptionLoaded: false,
  doneFetching: {
    episodes: false,
    extras: false,
  },
  spinnerVisible: true,
}

class SeasonsScreen extends React.Component {
  constructor(props) {
    super(props)
    this.componentType = 'Seasons'
    this.videoList = null
    this.button = null
    this.buttonTwo = null
    this.activeButtonIndex = 0
    this.buttonRefs = []
    this.showTitle = ''
    this.seasonTitle = ''
    this.readTTS = false
    this.eventKey = ''

    this.state = {
      activeSeason: '',
      seasonDescription: '',
      seasonDescriptionLoaded: false,
      spinnerVisible: true,
      seasons: [],
      videoSections: [],
      offset: 0,
      doneFetching: {
        episodes: false,
        extras: false,
      },
      videoListFocused: false,
      ga4PageViewParams: {},
    }
  }

  componentDidMount() {
    this.getSeasonData()
  }

  componentDidUpdate(prevProps, prevState) {
    const { seasonTts, seasonDescriptionTts } = this.state

    if (seasonTts && seasonDescriptionTts?.length > 0) {
      seasonDescriptionTts.unshift(seasonTts)
      tts.addTextToRead(seasonDescriptionTts)
      this.setState({
        seasonTts: undefined,
        seasonDescriptionTts: undefined,
      })
    } else if (seasonTts && this.eventKey === 'LEFT') {
      tts.addTextToRead(seasonTts)
      this.setState({
        seasonTts: undefined,
      })
    }

    const ga4PageViewParamsLength = Object.values(
      this.state.ga4PageViewParams
    ).length

    const iCanSendThePageViewEvent = ga4PageViewParamsLength === 2
    const sesasonHasChanged =
      this.state.ga4PageViewParams.season !== prevState.ga4PageViewParams.season
    if (iCanSendThePageViewEvent && sesasonHasChanged) {
      const { season, showTitle } = this.state.ga4PageViewParams
      googleAnalytics4.sendPageViewEvent(
        `Season | ${showTitle} | Season ${season}`
      )
    }
  }

  isDoneFetching() {
    const searchObject = this.state.searchObject
    const viewType =
      searchObject && searchObject.type && searchObject.type === 'extras'
        ? 'extras'
        : 'all'

    if (viewType == 'extras') {
      return this.state.doneFetching.extras
    } else {
      return this.state.doneFetching.episodes && this.state.doneFetching.extras
    }
  }

  onKeyEvent(key) {
    this.eventKey = key
    let handled = false
    const focusedComponent = window.focusedComponent
    const doneFecthing = this.isDoneFetching()

    if (focusedComponent.componentType) {
      tts.stopRead()
      if ('seasonButton' === focusedComponent.componentType) {
        if ('DOWN' === key) {
          if (this.activeButtonIndex < this.buttonRefs.length - 1) {
            this.navigate(key)
            this.setState(defaultState)

            this.debounced_getVideoData()
            handled = true
          } else {
            handled = true
          }
        }

        if ('UP' === key) {
          if (this.activeButtonIndex !== 0) {
            this.navigate(key)
            this.setState(defaultState)
            this.debounced_getVideoData()
            handled = true
          } else {
            handled = true
          }
        }

        if ('RIGHT' === key) {
          if (this.state.videoSections[0].videos.length > 0 && doneFecthing) {
            this.videoList?.setFocus()
            this.setState({ videoListFocused: true })
          } else {
            this.videoList?.instance?.emptySetMessageRef?.current?.setFocus()
            tts.addTextToRead(
              `No episodes are currently available for this season.`
            )
          }
          handled = true
        }
      }

      if ('video' === focusedComponent.componentType) {
        if ('LEFT' === key) {
          this.buttonRefs[this.activeButtonIndex]?.setFocus()
          this.setState({ videoListFocused: false })
          handled = true
        }
      }
    }

    if (handled === false) {
      if ('BACK' === key) {
        this.props.router.navigate(-1)
      }
    }

    return handled
  }

  navigate(direction) {
    // don't try to navigate if we can't find current activeRef
    if (
      this.buttonRefs[this.activeButtonIndex].instance.buttonRef.current ===
      null
    ) {
      return
    }

    let offset = this.state.offset

    switch (direction) {
      case 'UP':
        offset -= OFFSET
        this.activeButtonIndex--
        break
      case 'DOWN':
        offset += OFFSET
        this.activeButtonIndex++
        break
      default:
        break
    }
    this.setState({
      offset,
    })
    this.buttonRefs[this.activeButtonIndex]?.setFocus()
  }

  /**
   * Helper function to parse through query params and save them in state.
   */
  getParams(queries) {
    let searchObject = {}
    let items = queries.replace(/^\?/, '').split('&')
    for (let i = 0; i < items.length; i++) {
      let split = items[i].split('=')
      searchObject[split[0]] = split[1]
    }

    this.setState({ searchObject })

    return searchObject
  }

  /**
   * Helper function to look at available seasons and focus on the one specified.
   * Returns the ref for the focused season button.
   */
  getActiveSeason(season, buttonRefs) {
    let height
    for (let i = 0; i < buttonRefs.length; i++) {
      if (buttonRefs[i].props.season === parseInt(season, 10)) {
        this.activeButtonIndex = i
        if (buttonRefs[i].instance.buttonRef.current === null) {
          return
        } else {
          height = OFFSET
        }

        const offset = height * (i - 1)
        this.setState({ offset })

        return buttonRefs[i]
      }
    }
    // if we can't find the season you specified,
    // we should show you the first available season
    return buttonRefs[0]
  }

  /**
   * DATA REQUEST #1. Gets a list of a show's seasons with only surface-level data for each,
   * including season ordinal number, season image, and has_assets/has_episodes flags.
   * A separate call needs to be made to get the actual video assets belonging to a given season.
   */
  getSeasonData() {
    const slug = this.props.router.params.show
    getAvailableSeasons(slug, this.props.station_id)
      .then((resp) => {
        const params = this.getParams(
          decodeURI(this.props.router.location.search)
        )
        const seasons = resp.collections[0].content.filter(
          (season) => season.flags.has_assets || season.flags.has_episodes
        )

        this.setState(
          {
            seasons,
          },
          () => {
            let activeButton
            if (
              this.buttonRefs.length &&
              this.props.router.location.search.length > 0
            ) {
              const season = params.season
              activeButton = this.getActiveSeason(season, this.buttonRefs)
            } else if (this.buttonRefs.length) {
              activeButton = this.buttonRefs[this.activeButtonIndex]
            }
            activeButton?.setFocus()

            this.setState({ spinnerVisible: true })
            // get data about the videos within the active season
            this.debounced_getVideoData(activeButton)
          }
        )
      })
      .then(() => {
        // If we're looking at the "extras" season picker,
        // check for a collection of show-level extras. These are not affiliated with a given season,
        // and so they get the "More Extras" label at the bottom of the seasons list.
        const { searchObject } = this.state
        if (
          searchObject &&
          searchObject.type &&
          searchObject.type === 'extras'
        ) {
          this.getMoreExtras(slug)
        } else {
          return
        }
      })
      .catch((error) => {
        store.dispatch({
          type: 'TRIGGER_ERROR',
          payload: {
            error: {
              content: {
                error,
                errorPath: this.props.router.location.pathname,
              },
              codeBaseLocation: 'SeasonsScreen - getSeasonData',
              errorType: ComcastErrorType.network,
            },
          },
        })

        this.props.router.navigate('/error', { replace: true })
      })
  }

  /**
   * DATA REQUEST #2. Gets a collection of show-level extras to be displayed in the "More Extras"
   * heading. We only call this function if we're looking at the season picker extras-only view.
   */
  getMoreExtras(slug) {
    getMoreExtras(slug, this.props.station_id)
      .then(({ resp, url }) => {
        if (resp.collections[0].content.length > 0) {
          this.setState((prevState) => {
            let baseSeasons = prevState.seasons
            baseSeasons.push({
              item_type: 'more-extras',
              title: 'More Extras',
              flags: {
                has_assets: true,
                has_episodes: false,
              },
              extras_url: url,
            })
            return {
              seasons: baseSeasons,
            }
          })
        }
      })
      .catch(({ error, url }) => {
        const errorDescription = `Error ${
          error.status ? error.status : '(unknown)'
        } on GA Seasons screen checking for More Extras on ${url}`

        triggerSentryError('getMoreExtras', 'SeasonScreen.js', {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorDescription,
          errorLocation: 'SeasonsSreen - getMoreExtras',
          errorType: ComcastErrorType.network,
        })
      })
  }

  /**
   * DATA REQUEST #3. Gets the relevant season data to populate the video list
   * for the selected season. This can be a call to the extras endpoint alone
   * or to the episodes endpoint in addition to the extras endpoint. We also
   * include a call to the season details endpoint to retrieve the season
   * description, which is not available elsewhere.
   */
  getVideoData(activeButton = this.buttonRefs[this.activeButtonIndex]) {
    const { searchObject } = this.state
    this.seasonTitle = this.state.seasons[this.activeButtonIndex].title
    this.seasonId = this.state.seasons[this.activeButtonIndex].cid

    this.setState({
      activeSeason: activeButton.props.season,
      doneFecthing: {
        episodes: false,
        extras: false,
      },
    })

    // determine if we're looking at a season in the 'all' view
    // or in the 'extras' view

    const viewType =
      searchObject && searchObject.type && searchObject.type === 'extras'
        ? 'extras'
        : 'all'

    // if we're looking at extras only, our requests will be:
    // 1. to the /assets/ endpoint
    // 2. to the show-level extras endpoint
    if (viewType === 'extras') {
      // then get the extras for the selected season/tab
      getExtrasInSeasons(
        this.props.router.params.show,
        activeButton.props.cid,
        this.props.station_id,
        this.state.activeSeason,
        !activeButton.props.cid && activeButton.props.extrasUrl
      )
        .then((resp) => {
          let videos
          let hasVideos = true
          // The feed has been missing episodes from seasons
          // So here is a check for episodes...
          if (resp.collections.length === 0) {
            videos = []
            hasVideos = false
          } else {
            videos = resp.collections[0].content
          }
          if (hasVideos) {
            this.setShowTitle(videos)
          }
          let extras = {
            videos,
            hasVideos,
            type: 'extras',
          }
          this.setState((prevState) => ({
            spinnerVisible: true,
            videoSections: [extras],
            showTitle: this.showTitle,
            doneFetching: { ...prevState.doneFetching, extras: true },
          }))
        })
        .then(() => {
          //check for undefined here because we are calling this in the extra's context where seasonID is likely to not exist
          if (this.seasonId !== undefined) {
            this.getSeasonDetails(this.seasonId)
          } else {
            this.setState({
              spinnerVisible: false,
            })
          }
        })
        .catch(({ error, url }) => {
          let videos = []
          let hasVideos = false
          // if we error out trying to fetch extras for a given season, display empty message
          this.setState({
            spinnerVisible: false,
            videoSections: [
              {
                videos,
                hasVideos,
                type: 'extras',
              },
            ],
            showTitle: this.showTitle,
            doneFetching: { ...this.state.doneFetching, extras: true },
          })

          const errorDescription = `Error ${
            error.status ? error.status : '(unknown)'
          } on GA Seasons screen checking for Season Extras on ${url}`

          triggerSentryError('getExtrasInSeasons', 'SeasonsScreen.js', {
            errorMessage: error.message ?? 'Undefined error message (default)',
            errorDescription,
            errorLocation: 'SeasonsSreen - getExtrasInSeasons',
            errorType: ComcastErrorType.network,
          })
        })
    } else {
      // so, if the viewType is not 'extras', it's 'all'
      // meaning we're trying to get a season's worth of episodes AND extras
      // this is where videoSections become important, because it's how we'll
      // display two concatenated VideoLists

      // we want the episodes url request to fill in an array of episode videos,
      // and then the extras url request to fill in an array of extra videos,
      // and we want to add both those arrays to an array we call videoSections
      getPaginatedEpisodes(
        this.props.router.params.show,
        activeButton.props.cid,
        this.props.station_id,
        this.state.activeSeason
      )
        .then((paginatedEpisodes) => {
          this.setShowTitle(paginatedEpisodes)
          let episodes = {
            videos: [],
            hasVideos: false,
            type: 'episodes',
          }
          if (paginatedEpisodes.length > 0) {
            episodes = {
              videos: paginatedEpisodes,
              hasVideos: true,
              type: 'episodes',
            }
          }
          this.setState((prevState) => ({
            spinnerVisible: true,
            videoSections: [episodes],
            showTitle: this.showTitle,
            doneFetching: { ...prevState.doneFetching, episodes: true },
          }))
          // repeat this process for extras
        })
        .then(() => {
          this.getSeasonDetails(this.seasonId)
        })
        .then(() => {
          this.getSeasonExtras(activeButton.props.cid)
        })
        .catch(({ error, url }) => {
          // if we error on the episodes endpoint, display the empty message

          let episodes = {
            videos: [],
            hasVideos: false,
            type: 'episodes',
          }
          this.setState((prevState) => ({
            spinnerVisible: false,
            videoSections: [episodes],
            showTitle: this.showTitle,
            doneFetching: { ...prevState.doneFetching, episodes: true },
          }))

          const errorDescription = `Error ${
            error.status ? error.status : '(unknown)'
          } on GA Seasons screen checking for Season Episodes on ${url}`

          triggerSentryError('getPaginatedEpisodes', 'SeasonsScreen.js', {
            errorMessage: error.message ?? 'Undefined error message (default)',
            errorDescription,
            errorLocation: 'SeasonsSreen - getPaginatedEpisodes',
            errorType: ComcastErrorType.network,
          })

          // now try to fetch extras
          this.getSeasonExtras(activeButton.props.cid)
        })
    }
  }

  /**
   * DATA REQUEST #4. Gets detailed data about a particular season, including the
   * season's title and description, if they exist.
   */
  getSeasonDetails(seasonId) {
    getSeasonDetails(
      this.props.router.params.show,
      seasonId,
      this.props.station_id
    )
      .then((resp) => {
        this.setState({
          seasonDescription: resp.resource.description_long,
          seasonDescriptionLoaded: true,
          spinnerVisible: false,
        })
      })
      .catch(({ error, url }) => {
        const errorDescription = `Error ${
          error.status ? error.status : '(unknown)'
        } on GA Seasons screen checking for Season Details on ${url}`

        triggerSentryError('getSeasonDetails', 'SeasonsScreen.js', {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorDescription,
          errorLocation: 'SeasonsSreen - getSeasonDetails',
          errorType: ComcastErrorType.network,
        })
      })
  }

  /**
   * DATA REQUEST #5. Gets extras for a given season.
   */
  getSeasonExtras(seasonId) {
    getExtrasInSeasons(
      this.props.router.params.show,
      seasonId,
      this.props.station_id,
      this.state.activeSeason
    )
      .then((resp) => {
        let videos
        let hasVideos = true
        if (resp.collections.length === 0) {
          videos = []
          hasVideos = false
        } else {
          videos = resp.collections[0].content
        }

        if (hasVideos) {
          this.setShowTitle(videos)
        }

        let extras = {
          videos,
          hasVideos,
          type: 'extras',
        }
        this.setState((prevState) => ({
          videoSections: [...prevState.videoSections, extras],
          showTitle: this.showTitle,
          doneFetching: { ...prevState.doneFetching, extras: true },
        }))
      })
      .catch(({ error, url }) => {
        // if we error on the extras endpoint, display the empty message
        let videos = []
        let hasVideos = false

        let extras = {
          videos,
          hasVideos,
          type: 'extras',
        }

        this.setState((prevState) => ({
          videoSections: [...prevState.videoSections, extras],
          spinnerVisible: false,
          showTitle: this.showTitle,
          doneFetching: { ...prevState.doneFetching, extras: true },
        }))

        const errorDescription = `Error ${
          error.status ? error.status : '(unknown)'
        } on GA Seasons screen checking for Season Extras on ${url}`

        triggerSentryError('getSeasonExtras', 'SeasonsScreen.js', {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorDescription,
          errorLocation: 'SeasonsSreen - getSeasonExtras',
          errorType: ComcastErrorType.network,
        })
      })
  }

  debounced_getVideoData = debounce(this.getVideoData, 1000)

  setButtonRef(button, i) {
    //before referencing the season buttons, read out the show title once
    if (!this.readTTS) {
      tts.pushTextToRead(`${this.state.searchObject.parent} Seasons`)
      this.readTTS = true
    }

    if (button) {
      this.buttonRefs[i] = button
    }
  }

  setVideoListRef(videoList) {
    if (videoList !== null) {
      this.videoList = videoList
    }
  }

  /**
   * Determines which title string to display for Show
   *
   * @param videos { Array } the video content to extract title from
   */
  setShowTitle = (videos) => {
    if ('' === this.showTitle) {
      this.showTitle = videos[0]?.parent?.season
        ? videos[0]?.parent?.season?.show?.title
        : videos[0]?.parent?.show?.title
    } else {
      this.showTitle = this.state.searchObject.parent
    }

    this.setState((prevState) => ({
      ga4PageViewParams: {
        ...prevState.ga4PageViewParams,
        showTitle: this.showTitle,
        season: prevState.activeSeason,
      },
    }))
  }

  handleButtonFocused = ({ tts }) => {
    this.setState({
      seasonTts: tts,
    })
  }

  handleVideoListReadText = (tts) => {
    this.setState({
      seasonDescriptionTts: tts,
    })
  }

  render() {
    const {
      spinnerVisible,
      seasons,
      activeSeason,
      videoSections,
      offset,
      showTitle,
      seasonDescription,
      seasonDescriptionLoaded,
      videoListFocused,
    } = this.state

    const isDoneFetching = this.isDoneFetching()
    return (
      <div className="seasons-screen-container">
        <div className="sidebar-outer-container">
          <div className="header">
            <LogoHeader />
            <h2 className="sidebar-show-title">{showTitle}</h2>
          </div>
          <div className="sidebar-wrapper">
            <div
              className="sidebar-container"
              style={{
                transition: platformTransitionStyle(
                  'transform 250ms ease-in-out'
                ),
                transform: `translate(0px, -${
                  seasons?.length > 6 ? offset : 0
                }px)`,
              }}
            >
              {seasons.map((season, i) => {
                const isSelected =
                  season.ordinal === this.state.activeSeason && !this.hasFocus
                const buttonText = season.ordinal
                  ? `Season ${season.ordinal}`
                  : season.title
                return (
                  <StdButton
                    isSelected={isSelected}
                    parent={this}
                    key={i}
                    cid={season.cid}
                    extrasUrl={season.extras_url}
                    label={`Season ${season.ordinal}`}
                    season={season.ordinal}
                    link={false}
                    componentType="seasonButton"
                    ref={(button) => {
                      this.setButtonRef(button, i)
                    }}
                    preventReadTextOnFocus={true}
                    onFocus={this.handleButtonFocused}
                  >
                    {buttonText}
                  </StdButton>
                )
              })}
            </div>
          </div>
        </div>

        {isDoneFetching && seasonDescriptionLoaded ? (
          <VideoList
            parent={this}
            videoSections={videoSections}
            activeSeason={activeSeason}
            seasonShowTitle={this.seasonTitle}
            seasonDescription={seasonDescription}
            videoListFocus={videoListFocused}
            ref={(videoList) => this.setVideoListRef(videoList)}
            spinnerVisible={spinnerVisible}
            onReadText={this.handleVideoListReadText}
          />
        ) : (
          <Spinner visible={spinnerVisible} mini={false} />
        )}
      </div>
    )
  }
}

SeasonsScreen.propTypes = {
  router: PropTypes.shape({
    navigate: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    params: PropTypes.shape({
      show: PropTypes.string,
    }),
  }),
  station_id: PropTypes.string,
}

const mapStateToProps = (state) => {
  return {
    station_id: state.station.station_id,
  }
}

export default connect(
  mapStateToProps,
  null,
  null
)(withRouter(focusable(SeasonsScreen)))
